import { type InfiniteData, useMutation } from '@tanstack/react-query';

import { Collections } from '@air/api';
import { type ListResponse, type ShortUrlResponse } from '@air/api/types';
import { type FullWorkspace } from '@air/types';
import { callAirApi } from '@air/utils-api';
import { isAirrorInstance, reportErrorToBugsnag } from '@air/utils-error';

import { updateCollectionNameInListCache } from '~/components/SharePrivateCollectionModal/hooks/mutations/utils';
import { getCollectionKey } from '~/components/SharePrivateCollectionModal/hooks/queries/useGetCollection';
import { queryClient } from '~/swr-hooks/queryClient';
import { isShortUrlsListKey } from '~/swr-hooks/shortUrl/useListShortUrls';

export type UseUpdateCollectionMutationParams = {
  workspaceId: FullWorkspace['id'];
  collectionId: string;
  updates: Omit<Required<Parameters<typeof Collections.update>>[0], 'workspaceId' | 'collectionId'>['updates'];
};

export const useUpdateCollectionMutation = () => {
  return useMutation({
    mutationFn: async (params: UseUpdateCollectionMutationParams) => callAirApi(() => Collections.update(params)),
    onMutate: async ({ collectionId, updates }) => {
      const queryKey = getCollectionKey({ collectionId });
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old || isAirrorInstance(old)) {
          return old;
        }

        return { ...old, ...updates };
      });

      return { previousData, collectionId };
    },
    onSuccess: (_data, { workspaceId, collectionId, updates }) => {
      if (workspaceId && updates.name) {
        queryClient.setQueriesData<InfiniteData<ListResponse<ShortUrlResponse>> | undefined>(
          {
            predicate: ({ queryKey }) => isShortUrlsListKey(queryKey) && queryKey[1].workspaceId === workspaceId,
          },
          (old) => (old ? updateCollectionNameInListCache(collectionId, updates, old) : old),
        );
      }
    },
    onError: (error, { collectionId }, context) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to update collection short URL',
        metadata: { data: { collectionId } },
      });

      if (context?.previousData) {
        queryClient.setQueryData(getCollectionKey({ collectionId: context.collectionId }), context.previousData);
      }
    },
    onSettled: (_data, _error, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: getCollectionKey({ collectionId }) });
    },
  });
};
